import React, { type FC, memo } from "react";
import { shallowEqual } from "react-redux";
import clsx from "clsx";
import Text from "../../../../../../shared-components/css-modules/lexus/atoms/Text";
import { type CarConfigDispatchType, useCarConfigSelector } from "../../../../../redux/store";
import { getInsuranceQuoteIdFromStorage, getSessionStorageParams } from "../../../../../utils/sessionStorage";
import Button from "../../../../../../shared-components/css-modules/lexus/atoms/Button";
import InsurancePrice from "../../../common/price/InsurancePrice";
import { useShowAfterFirstRender } from "../../../../../../../../common-deprecated/hooks";
import styles from "./Services.module.scss";
import AddToBasket from "../../../common/AddToBasket";
import { trackRemoveInsurance } from "../../../../../utils/tracking";
import { clearInsuranceRate } from "../../../../../redux/slices/CarConfigInsuranceSlice";
import InsuranceDescriptionList from "./InsuranceDescriptionList";
import useAphModal from "../../../../../hooks/useAphModal";
import { MODAL_INSURANCE_CALCULATOR, MODAL_INSURANCE_DISCLAIMER } from "../../../../../utils/modalConstants";
import { CarConfigLabel } from "../../../../../utils/labelUtils";
import { useDispatch } from "../../../../../../../../shared-logic/redux/store";

const Insurance: FC = () => {
    const dispatch = useDispatch<CarConfigDispatchType>();

    const modelId = useCarConfigSelector((state) => state.car.model.id);
    const sessionStorageParams = useCarConfigSelector(getSessionStorageParams, shallowEqual);
    const insuranceQuoteId = getInsuranceQuoteIdFromStorage(sessionStorageParams, modelId);

    // This is needed, as quoteId is read from storage, there is a hydration error on page load
    // Do not remove, only when fixed with other method!
    const active = useShowAfterFirstRender();

    const insurance = useCarConfigSelector((state) => state.insurance);

    const { showModalHelper } = useAphModal();

    const hasDisclaimer = !!insurance.info.disclaimer?.table || insurance.info.disclaimer?.value;

    return (
        <>
            {insuranceQuoteId && active ? (
                <div className={styles.serviceblock}>
                    <header className={styles.flex}>
                        <Text tag="h3" variant="apheleia-body" spacing="spacing-1">
                            <strong>
                                <CarConfigLabel label="carConfigInsuranceTitle" />
                            </strong>
                        </Text>
                        <Button variant="apheleia-text" onClick={() => showModalHelper(MODAL_INSURANCE_CALCULATOR, {})}>
                            <CarConfigLabel label="carConfigInsuranceEditCta" />
                        </Button>
                    </header>
                    <Text variant="apheleia-body" tag="div" className={clsx(styles.serviceinfo, styles.insuranceinfo)}>
                        <div className={styles.flex}>
                            <div>{insurance.name}</div>
                            <div className={styles.price}>
                                <InsurancePrice />
                            </div>
                        </div>
                    </Text>

                    <InsuranceDescriptionList list={insurance.quoteInfo.Description_List?.value} />

                    {hasDisclaimer && (
                        <>
                            <Button
                                variant="apheleia-text"
                                onClick={() => showModalHelper(MODAL_INSURANCE_DISCLAIMER, {})}
                            >
                                <CarConfigLabel label="carConfigInsuranceTabMoreInfo" />
                            </Button>
                        </>
                    )}

                    <footer className={styles.footer}>
                        <AddToBasket
                            itemName="insurance"
                            active
                            onClick={() => {
                                trackRemoveInsurance();
                                dispatch(clearInsuranceRate());
                            }}
                        />
                    </footer>
                </div>
            ) : (
                <div className={styles.serviceblock}>
                    <Text tag="h3" variant="apheleia-body" spacing="spacing-1">
                        <strong>
                            <CarConfigLabel label="carConfigInsuranceTitle" />
                        </strong>
                    </Text>
                    <Text tag="p" variant="apheleia-body" spacing="spacing-1">
                        <CarConfigLabel label="carConfigInsuranceText" />
                    </Text>

                    <Button variant="apheleia-text" onClick={() => showModalHelper(MODAL_INSURANCE_CALCULATOR, {})}>
                        <CarConfigLabel label="carConfigInsuranceCta" />
                    </Button>
                </div>
            )}
        </>
    );
};

export default memo(Insurance);
